import { ListDriveInfo, ListSensorInfo } from '@ouster/webviz-react-components';

import { useMemo, useState } from 'react';

import { useDebouncedValue } from './useDebouncedValue';

const DEBOUNCE_DELAY = 150;

type ParsedSearchTerm = {
  term: string;
  hwOnly?: boolean;
};

const isHwField = (field: string) => field.toLowerCase().includes('hw');

const normalizeSearchValue = (value: unknown) =>
  value?.toString().toLowerCase() ?? '';

const normalizeHwTerm = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^rev\s*/, '');

const parseSearchTerms = (searchTerm: string): ParsedSearchTerm[] => {
  const rawTerms = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
  const parsedTerms: ParsedSearchTerm[] = [];

  for (let index = 0; index < rawTerms.length; index += 1) {
    const current = rawTerms[index];
    const next = rawTerms[index + 1];

    if ((current === 'rev' || current === 'hw') && next) {
      parsedTerms.push({ term: next, hwOnly: true });
      index += 1;
    } else {
      const prefixedHwMatch = current.match(/^(?:rev|hw)(.+)$/);
      if (prefixedHwMatch?.[1]) {
        parsedTerms.push({ term: prefixedHwMatch[1], hwOnly: true });
      } else {
        parsedTerms.push({ term: current });
      }
    }
  }

  return parsedTerms;
};

export const useRecordingSearch = (
  data: ListDriveInfo[],
  filterFields: string[],
  initialSearchTerm = ''
) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, DEBOUNCE_DELAY);

  const filteredData = useMemo(() => {
    // start filter when search term's length is greater than 2
    if (!debouncedSearchTerm || debouncedSearchTerm.length <= 2) return data;

    const searchTerms = parseSearchTerms(debouncedSearchTerm);
    if (searchTerms.length === 0) return data;

    /*
      For multi-sensor support, the sensor information are moved to sensor array
      Hence, to filter the data, we need to go deep and check the sensor array as well.
      Each space-separated word must match at least one field (AND logic).
    */
    return data.filter((item) =>
      searchTerms.every(({ term, hwOnly }) =>
        filterFields.some((field) => {
          if (hwOnly && !isHwField(field)) return false;

          const isSensorField =
            item.sensor_info?.some((sensor) => sensor && field in sensor) ??
            false;

          if (isSensorField) {
            // Field belongs to ListSensorInfo, check inside sensor_info
            return item.sensor_info?.some((sensor) =>
              isHwField(field)
                ? normalizeHwTerm(
                    normalizeSearchValue(sensor[field as keyof ListSensorInfo])
                  ).includes(normalizeHwTerm(term))
                : normalizeSearchValue(
                    sensor[field as keyof ListSensorInfo]
                  ).includes(term)
            );
          }
          // Field belongs to ListDriveInfo, check on item
          return isHwField(field)
            ? normalizeHwTerm(
                normalizeSearchValue(item[field as keyof ListDriveInfo])
              ).includes(normalizeHwTerm(term))
            : normalizeSearchValue(item[field as keyof ListDriveInfo]).includes(
                term
              );
        })
      )
    );
  }, [data, filterFields, debouncedSearchTerm]);

  return { searchTerm, setSearchTerm, filteredData };
};
